// Types and utilities for the analytics fact table system.
// The fact table catalog is fetched from GET /api/analytics/analytics_queries/v2/fact_tables
// and provides columns, filters, dimensions, and custom metrics per fact table.

// --- Types ---

export interface ColumnMeta {
  name: string
  type: 'all' | 'boolean' | 'timestamp' | 'string' | 'numeric' | 'entity'
  operations: string[]
  label?: string
  knownValues?: string[]
  entityKind?: string
}

export interface CustomMetricMeta {
  id: string
  label?: string
}

export interface QuickMetricMeta {
  id: string
  label: string
  column?: string
  operation?: string
  value?: string
  customMetric?: string
}

export interface Filter {
  id: string
  columnName?: string
  kind: string
  label?: string
  entityKind?: string
  multi?: boolean
  clientOnly?: boolean
  options?: Array<{ value: string; label: string }>
}

export interface Dimension {
  id: string
  kind: string
  columnName?: string
  label?: string
  name?: string
  group?: string
  geo?: string
  expression?: string
  tableName?: string
  options?: Array<{ id: number; name: string }>
}

export interface FactTable {
  kind: string
  label: string
  columns: ColumnMeta[]
  customMetrics: CustomMetricMeta[]
  quickMetrics?: QuickMetricMeta[]
  filters: Filter[]
  dimensions: Dimension[]
  segments: string[]
}

// Metric types — what the frontend sends to the API

export interface ColumnMetric {
  id: string
  factTable: string
  column: string
  operation: string
  value?: string
  label?: string
  cumulative?: boolean
}

export interface CustomMetricRef {
  id: string
  factTable: string
  customMetric: string
  label?: string
  cumulative?: boolean
}

export interface RatioMetric {
  id: string
  operation: 'ratio'
  numerator: string
  denominator: string
  label?: string
}

export type Metric = ColumnMetric | CustomMetricRef | RatioMetric

// Type guards

export function isRatioMetric(m: Metric): m is RatioMetric {
  return (m as RatioMetric).operation === 'ratio'
}

export function isCustomMetricRef(m: Metric): m is CustomMetricRef {
  return !!(m as CustomMetricRef).customMetric
}

export function isColumnMetric(m: Metric): m is ColumnMetric {
  return !isRatioMetric(m) && !isCustomMetricRef(m)
}

// --- Quick metric expansion ---

export function expandQuickMetric(qm: QuickMetricMeta, factTableKind: string): Metric {
  if (qm.customMetric) {
    return { id: '', factTable: factTableKind, customMetric: qm.customMetric, label: qm.label } as CustomMetricRef
  }
  const metric: ColumnMetric = { id: '', factTable: factTableKind, column: qm.column!, operation: qm.operation!, label: qm.label }
  if (qm.value) metric.value = qm.value
  return metric
}

// --- Operation labels ---

export const OPERATION_LABELS: Record<string, string> = {
  count: 'Count',
  count_non_null: 'Count Non-Null',
  count_null: 'Count Null',
  count_distinct: 'Count Distinct',
  count_true: 'Count True',
  count_false: 'Count False',
  count_where: 'Count Where',
  sum: 'Sum',
  avg: 'Average',
  min: 'Min',
  max: 'Max',
}

// --- Utility functions ---

/**
 * Extract unique fact table kinds referenced by an array of metrics.
 * Ratio metrics are skipped since they reference other metrics, not fact tables.
 */
export function referencedFactTableKinds(metrics: Metric[]): string[] {
  const kinds = new Set<string>()
  for (const m of metrics) {
    if (!isRatioMetric(m) && (m as ColumnMetric | CustomMetricRef).factTable) {
      kinds.add((m as ColumnMetric | CustomMetricRef).factTable)
    }
  }
  return [...kinds]
}

/**
 * Compute available dimensions as the intersection across all referenced fact tables.
 * A dimension is available only if every referenced fact table supports it (matched by id).
 * When no fact tables are referenced, returns an empty array.
 */
export function computeAvailableDimensions(
  factTables: FactTable[],
  referencedKinds: string[]
): Dimension[] {
  if (referencedKinds.length === 0) return []

  const dimSets = referencedKinds.map(kind => {
    const ft = factTables.find(f => f.kind === kind)
    return ft?.dimensions || []
  })

  // Intersection: keep dimensions whose id appears in ALL referenced tables
  const first = dimSets[0]
  return first.filter(dim =>
    dimSets.every(set => set.some(d => d.id === dim.id))
  )
}

/**
 * Compute available filters as the union across all referenced fact tables.
 * Deduplicated by filter id + kind. Filters with clientOnly=true are excluded
 * when singleClient is false.
 */
export function computeAvailableFilters(
  factTables: FactTable[],
  referencedKinds: string[],
  singleClient: boolean
): Filter[] {
  const seen = new Set<string>()
  const result: Filter[] = []

  for (const kind of referencedKinds) {
    const ft = factTables.find(f => f.kind === kind)
    for (const filter of ft?.filters || []) {
      if (filter.clientOnly && !singleClient) continue
      const key = `${filter.id}-${filter.kind}`
      if (!seen.has(key)) {
        seen.add(key)
        result.push(filter)
      }
    }
  }

  return result
}

/**
 * Generate a unique metric id that doesn't collide with existing metrics.
 * Produces m1, m2, m3, ...
 */
export function generateMetricId(existingMetrics: Metric[]): string {
  const usedNumbers = new Set<number>()
  for (const m of existingMetrics) {
    const match = m.id.match(/^m(\d+)$/)
    if (match) usedNumbers.add(parseInt(match[1], 10))
  }
  let n = 1
  while (usedNumbers.has(n)) n++
  return `m${n}`
}

/**
 * Generate a human-readable label for a metric based on its components and fact table metadata.
 */
export function metricDisplayLabel(metric: Metric, factTables: FactTable[]): string {
  if (metric.label) return metric.label

  if (isRatioMetric(metric)) {
    return `${metric.numerator} / ${metric.denominator}`
  }

  const ft = factTables.find(f => f.kind === (metric as ColumnMetric | CustomMetricRef).factTable)
  const ftLabel = ft?.label || (metric as ColumnMetric | CustomMetricRef).factTable

  if (isCustomMetricRef(metric)) {
    const cm = ft?.customMetrics?.find(c => c.id === metric.customMetric)
    return cm?.label ? `${ftLabel} — ${cm.label}` : `${ftLabel} — ${metric.customMetric}`
  }

  const col = metric as ColumnMetric
  const colMeta = ft?.columns?.find(c => c.name === col.column)
  const colLabel = colMeta?.label || col.column
  const opLabel = OPERATION_LABELS[col.operation] || col.operation

  if (col.column === '*') return `${ftLabel} — ${opLabel}`
  if (col.operation === 'count_where' && col.value) {
    return `${ftLabel} — ${colLabel} = ${col.value}`
  }
  return `${ftLabel} — ${opLabel}(${colLabel})`
}
